import { decodeJwtPayload } from '@/utils/authMapper';

const TOKEN_STORAGE_KEYS = ['wecom_token', 'cdp_token', 'access_token'];
const URL_TOKEN_KEYS = ['token', 'access_token'];

function normalizeToken(raw) {
  if (!raw || typeof raw !== 'string') return '';
  const trimmed = raw.trim();
  if (!trimmed) return '';
  return trimmed.startsWith('Bearer ') ? trimmed.slice(7).trim() : trimmed;
}

function isMockToken(token) {
  return typeof token === 'string' && token.startsWith('mock_token_');
}

/**
 * JWT 已过期时返回 true。
 * 带着过期凭证请求会让所有接口 401，页面表现为「数据全空 / 模板打不开」，
 * 因此这里提前判定并当作无 Token 处理，让流程回到登录页。
 */
export function isTokenExpired(token) {
  const exp = Number(decodeJwtPayload(token)?.exp);
  if (!Number.isFinite(exp) || exp <= 0) return false;
  return Date.now() >= exp * 1000;
}

/**
 * .env.local 中配置的联调 Token，仅本地 dev server 生效。
 * 打包产物不得携带个人凭证，否则会随 dist 发布，并在过期后让整站 401。
 */
export function getEnvAuthToken() {
  if (!import.meta.env.DEV || !import.meta.hot) return '';
  return normalizeToken(import.meta.env.VITE_API_ACCESS_TOKEN || '');
}

function readTokenFromSearchParams(searchParams) {
  if (!searchParams) return '';
  for (const key of URL_TOKEN_KEYS) {
    const val = normalizeToken(searchParams.get(key));
    if (val) return val;
  }
  return '';
}

/** 从页面 URL 或 hash 路由 query 中读取 token / access_token */
export function readAuthTokenFromUrl(href = typeof window !== 'undefined' ? window.location.href : '') {
  if (!href) return '';

  try {
    const url = new URL(href, typeof window !== 'undefined' ? window.location.origin : 'http://localhost');
    const fromSearch = readTokenFromSearchParams(url.searchParams);
    if (fromSearch) return fromSearch;

    const hash = url.hash || '';
    const qIndex = hash.indexOf('?');
    if (qIndex >= 0) {
      return readTokenFromSearchParams(new URLSearchParams(hash.slice(qIndex + 1)));
    }
  } catch {
    return '';
  }

  return '';
}

/** 从地址栏移除 token 参数，避免泄露到历史记录或分享链接 */
export function stripAuthTokenFromUrl(href = typeof window !== 'undefined' ? window.location.href : '') {
  if (!href || typeof window === 'undefined') return href;

  try {
    const url = new URL(href, window.location.origin);
    let changed = false;

    URL_TOKEN_KEYS.forEach((key) => {
      if (url.searchParams.has(key)) {
        url.searchParams.delete(key);
        changed = true;
      }
    });

    const hash = url.hash || '';
    const qIndex = hash.indexOf('?');
    if (qIndex >= 0) {
      const path = hash.slice(0, qIndex);
      const hashParams = new URLSearchParams(hash.slice(qIndex + 1));
      URL_TOKEN_KEYS.forEach((key) => {
        if (hashParams.has(key)) {
          hashParams.delete(key);
          changed = true;
        }
      });
      const rest = hashParams.toString();
      url.hash = rest ? `${path}?${rest}` : path;
    }

    if (changed) {
      window.history.replaceState(window.history.state, '', url.toString());
      return url.toString();
    }
  } catch {
    return href;
  }

  return href;
}

/**
 * 启动时应用 URL 中的 token（支持 ?token= 与 #/path?token=）
 * 写入 localStorage 后从地址栏移除，避免 token 长期暴露在 URL 中。
 */
export function applyAuthTokenFromUrl() {
  const token = readAuthTokenFromUrl();
  if (!token) return false;
  setAuthToken(token);
  stripAuthTokenFromUrl();
  return true;
}

export function isUserLoggedOut() {
  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('user_logged_out') === '1') return true;
  if (typeof localStorage !== 'undefined' && localStorage.getItem('user_logged_out') === '1') return true;
  return false;
}

export function clearLogoutFlag() {
  if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem('user_logged_out');
  if (typeof localStorage !== 'undefined') localStorage.removeItem('user_logged_out');
}

/**
 * 读取当前可用 Token（从企微登录存储动态获取，不再硬编码）
 */
export function getAuthToken() {
  if (isUserLoggedOut()) return '';
  if (typeof localStorage !== 'undefined') {
    for (const key of TOKEN_STORAGE_KEYS) {
      const val = normalizeToken(localStorage.getItem(key));
      if (val && !isTokenExpired(val)) return val;
    }
  }
  const envToken = getEnvAuthToken();
  return envToken && !isTokenExpired(envToken) ? envToken : '';
}


/** 构建带 Authorization 的请求头 */
export function getAuthHeaders(extraHeaders = {}) {
  const headers = { ...extraHeaders };
  const token = getAuthToken();
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
}

/** 持久化登录 Token（mock Token 不落盘） */
export function setAuthToken(token) {
  clearLogoutFlag();
  const normalized = normalizeToken(token);
  if (!normalized || typeof localStorage === 'undefined') return;
  if (isMockToken(normalized)) return;
  localStorage.setItem('wecom_token', normalized);
  localStorage.setItem('cdp_token', normalized);
}

/** 清除登录 Token */
export function clearAuthToken() {
  if (typeof localStorage !== 'undefined') {
    TOKEN_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
    localStorage.setItem('user_logged_out', '1');
  }
  if (typeof sessionStorage !== 'undefined') {
    sessionStorage.setItem('user_logged_out', '1');
  }
}
